/**
 * useExecutionProofs — read-only access to website-uploaded execution
 * proof photos/documents.
 *
 * Scope (beta, read-only pass):
 *   • Fetches `execution_proofs` rows via the existing thin
 *     wrappers in `src/lib/supabase/executions.ts`.
 *   • Resolves either an explicit `executionId` OR an APP `appJobId`
 *     (which may map to 0, 1, or N executions — all are aggregated).
 *   • Lazily mints signed read URLs via `requestExecutionProofReadUrl`
 *     and caches them in a module-level Map keyed by proofId with a
 *     conservative TTL so repeat opens don't re-hit Storage/function reads.
 *
 * Explicit non-goals (per audit approval):
 *   • No writes. No upload. No supersede flips.
 *   • No merge with `JobSpecExecution.photos[]` (prototype model).
 *   • No reconciliation, no schema changes, no new tables.
 *   • Phase grouping is NOT derivable from the canonical schema today
 *     (no `phase` column on `execution_proofs`), so callers
 *     should group by `kind` or display flat. Phase binding is deferred.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  listExecutionsByAppJobId,
  listExecutionProofs,
  listExecutionProofsByAppJobId,
  requestExecutionProofReadUrl,
  type ExecutionProof,
  type PortalExecution,
} from "@/lib/supabase/executions";

export interface UseExecutionProofsArgs {
  /** Direct portal execution id. Takes priority over appJobId when both set. */
  executionId?: string;
  /** APP-side job id — resolves to all portal executions for that job. */
  appJobId?: string;
  /** Skip the fetch entirely (e.g. tab not yet opened). */
  enabled?: boolean;
}

export interface UseExecutionProofsResult {
  proofs: ExecutionProof[];
  /** All matching executions (>0 when appJobId resolves to multiple). */
  executions: PortalExecution[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  /**
   * Fetch (and cache) a signed read URL for a single proof. Returns null
   * on failure / not_configured. Cached for ~9 minutes per proofId.
   */
  getReadUrl: (proofId: string) => Promise<string | null>;
}

// Default signed URL TTL on the edge function is 600s. Cache slightly less.
const SIGNED_URL_CACHE_TTL_MS = 9 * 60 * 1000;
const READ_URL_EXPIRES_SECONDS = 600;

interface CachedUrl {
  url: string;
  expiresAt: number;
}

// Module-level cache shared across all gallery mounts in the session.
const signedUrlCache = new Map<string, CachedUrl>();
const inflightRequests = new Map<string, Promise<string | null>>();

function pruneCache(now: number) {
  for (const [id, entry] of signedUrlCache) {
    if (entry.expiresAt <= now) signedUrlCache.delete(id);
  }
}

export function useExecutionProofs(
  args: UseExecutionProofsArgs,
): UseExecutionProofsResult {
  const { executionId, appJobId, enabled = true } = args;

  const [executions, setExecutions] = useState<PortalExecution[]>([]);
  const [proofs, setProofs] = useState<ExecutionProof[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const refresh = useCallback(() => setNonce((n) => n + 1), []);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    if (!enabled || (!executionId && !appJobId)) {
      setExecutions([]);
      setProofs([]);
      return;
    }

    setLoading(true);
    setError(null);

    (async () => {
      try {
        const execList: PortalExecution[] = executionId
          ? []
          : await listExecutionsByAppJobId(appJobId!);

        const flat = executionId
          ? await listExecutionProofs(executionId)
          : await listExecutionProofsByAppJobId(appJobId!);

        if (cancelledRef.current) return;
        setExecutions(execList);
        setProofs(flat);
      } catch (e) {
        if (cancelledRef.current) return;
        setError(e instanceof Error ? e.message : "Failed to load proofs.");
      } finally {
        if (!cancelledRef.current) setLoading(false);
      }
    })();

    return () => {
      cancelledRef.current = true;
    };
  }, [executionId, appJobId, enabled, nonce]);

  const getReadUrl = useCallback(async (proofId: string) => {
    const now = Date.now();
    pruneCache(now);

    const cached = signedUrlCache.get(proofId);
    if (cached && cached.expiresAt > now) return cached.url;

    const existing = inflightRequests.get(proofId);
    if (existing) return existing;

    const promise = (async () => {
      const res = await requestExecutionProofReadUrl(
        proofId,
        READ_URL_EXPIRES_SECONDS,
      );
      if (!res.ok) return null;
      signedUrlCache.set(proofId, {
        url: res.readUrl,
        expiresAt: Date.now() + SIGNED_URL_CACHE_TTL_MS,
      });
      return res.readUrl;
    })().finally(() => {
      inflightRequests.delete(proofId);
    });

    inflightRequests.set(proofId, promise);
    return promise;
  }, []);

  return useMemo(
    () => ({ proofs, executions, loading, error, refresh, getReadUrl }),
    [proofs, executions, loading, error, refresh, getReadUrl],
  );
}
